import React from 'react';
import type { PodcastChannel } from '../../data/podcast';

type IconName = NonNullable<PodcastChannel['icon']>;

/**
 * Platform glyphs for the channel buttons. Each path is drawn on a 24x24
 * grid and inherits the button's text colour via `currentColor`.
 */
const PATHS: Record<IconName, React.ReactNode> = {
  spotify: (
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.871 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857Zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 0 1-.452-1.492c3.632-1.102 8.147-.568 11.232 1.329a.78.78 0 0 1 .257 1.072Zm.105-2.835C14.697 8.96 9.735 8.78 6.9 9.641a.935.935 0 1 1-.543-1.79c3.254-.988 8.734-.797 12.177 1.246a.935.935 0 1 1-.955 1.608c-.004 0-.004 0 0 0Z" />
  ),
  youtube: (
    <path d="M21.582 7.186a2.506 2.506 0 0 0-1.768-1.768C18.254 5 12 5 12 5s-6.254 0-7.814.418c-.86.23-1.538.908-1.768 1.768C2 8.746 2 12 2 12s0 3.254.418 4.814c.23.86.908 1.538 1.768 1.768C5.746 19 12 19 12 19s6.254 0 7.814-.418a2.506 2.506 0 0 0 1.768-1.768C22 15.254 22 12 22 12s0-3.254-.418-4.814ZM10 15.464V8.536L15.964 12 10 15.464Z" />
  ),
  // Microphone, after the Apple Podcasts mark: capsule, wrapping arc, stand.
  apple: (
    <>
      <path d="M12 1.6a3.3 3.3 0 0 0-3.3 3.3v6.3a3.3 3.3 0 0 0 6.6 0V4.9A3.3 3.3 0 0 0 12 1.6Z" />
      <path d="M17.9 10.2a.95.95 0 0 0-1.9 0 4 4 0 0 1-8 0 .95.95 0 0 0-1.9 0 5.95 5.95 0 0 0 5 5.87v2.06h-1.7a.95.95 0 0 0 0 1.9h5.3a.95.95 0 0 0 0-1.9h-1.7v-2.06a5.95 5.95 0 0 0 4.9-5.87Z" />
    </>
  ),
  amazon: (
    <path d="M18.3 16.9c-2.2 1.6-5.4 2.5-8.1 2.5-3.8 0-7.3-1.4-9.9-3.8-.2-.2 0-.4.2-.3 2.8 1.6 6.3 2.6 9.9 2.6 2.5 0 5.2-.5 7.7-1.6.4-.2.7.2.2.6Zm.9-1.1c-.3-.4-1.9-.2-2.6-.1-.2 0-.3-.2-.1-.3 1.3-.9 3.4-.6 3.7-.3.3.3-.1 2.4-1.3 3.4-.2.2-.4.1-.3-.1.3-.7.9-2.2.6-2.6ZM16.8 8.7V7.8c0-.2.1-.3.3-.3h4c.2 0 .3.1.3.3v.8c0 .2-.1.4-.4.7l-2.1 3c.8 0 1.6.1 2.3.5.2.1.2.2.2.4v.9c0 .2-.2.4-.4.3a4.6 4.6 0 0 0-4.2 0c-.2.1-.4-.1-.4-.3v-.9c0-.2 0-.5.2-.8l2.4-3.4h-2.1c-.2 0-.3-.1-.3-.3ZM6.2 14.6h-1.2c-.1 0-.2-.1-.2-.2V7.8c0-.1.1-.2.2-.2h1.1c.1 0 .2.1.2.2v.9h.1c.3-.8.9-1.2 1.6-1.2.8 0 1.3.4 1.6 1.2.3-.8 1-1.2 1.8-1.2.5 0 1.1.2 1.4.7.4.6.3 1.4.3 2.1v4.1c0 .1-.1.2-.2.2h-1.2c-.1 0-.2-.1-.2-.2v-3.5c0-.3 0-1 0-1.3-.1-.4-.4-.6-.8-.6-.3 0-.7.2-.8.6-.2.4-.1 1-.1 1.3v3.5c0 .1-.1.2-.2.2H8.4c-.1 0-.2-.1-.2-.2v-3.5c0-.7.1-1.9-.8-1.9-.9 0-.9 1.1-.9 1.9v3.5c0 .1-.1.2-.3.2Z" />
  ),
};

interface ChannelIconProps {
  name: IconName;
  className?: string;
}

export const ChannelIcon: React.FC<ChannelIconProps> = ({ name, className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    {PATHS[name]}
  </svg>
);
